/**
 * Competizioni (V42): della societa, aperte ad Admin, Mister e Dirigente con
 * le stesse azioni. Si creano, si rinominano, si disattivano; si eliminano
 * solo finche nessuna partita le usa. Una disattivata resta sulle sue partite
 * e nei filtri di statistiche e scheda giocatore, ma non si propone per le
 * partite nuove.
 */

import { type FormEvent, useState } from 'react';
import { InfoIcon, OctagonXIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import {
  creaCompetizioneSchema,
  modificaCompetizioneSchema,
  nomeConAnno,
  normalizzaNome,
} from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MenuAzioni, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { AVVISO_ANNO } from './avvisi.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { useConferma } from '../comuni/Conferma.js';

interface Competizione {
  id: string;
  nome: string;
  attiva: boolean;
  partite: number;
}

/** Null: dialog chiuso. Senza id: nuova competizione. */
type InModifica = Partial<Competizione> | null;

export function Competizioni() {
  const { dati: competizioni, errore, ricarica } = useDati<Competizione[]>('/competizioni');
  const [inModifica, setInModifica] = useState<InModifica>(null);
  const [chiedi, conferma] = useConferma();

  const nuova = <PulsanteNuovo etichetta="Nuova competizione" onClick={() => setInModifica({})} />;

  const elimina = async (c: Competizione) => {
    const si = await chiedi({
      titolo: `Eliminare ${c.nome}?`,
      azione: 'Elimina',
      distruttiva: true,
    });
    if (!si) return;
    try {
      await chiama(`/competizioni/${c.id}`, { metodo: 'DELETE' });
      await ricarica();
      toast.success(`${c.nome} eliminata.`);
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  if (!competizioni)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  return (
    <>
      <Testata
        titolo="Competizioni"
        voce="competizioni"
        azione={competizioni.length > 0 && nuova}
      />
      <Corpo>
        {competizioni.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nessuna competizione</EmptyTitle>
              <EmptyDescription>
                Ogni partita appartiene a una competizione, come «Campionato» o «Coppa»: serve a
                filtrare statistiche e scheda giocatore.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>{nuova}</EmptyContent>
          </Empty>
        ) : (
          <div className="flex flex-col gap-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="text-right">Partite</TableHead>
                  <TableHead>
                    <span className="sr-only">Azioni</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {competizioni.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {c.nome}
                        {!c.attiva && <Badge variant="secondary">Disattivata</Badge>}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{c.partite}</TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <MenuAzioni
                          riga
                          etichetta={`Azioni su ${c.nome}`}
                          voci={[
                            {
                              etichetta: 'Modifica',
                              Icona: PencilIcon,
                              onSelect: () => setInModifica(c),
                            },
                            {
                              etichetta: 'Elimina',
                              Icona: Trash2Icon,
                              disabled: c.partite > 0,
                              motivo: 'Ha partite: non si elimina.',
                              onSelect: () => void elimina(c),
                              distruttiva: true,
                            },
                          ]}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <Dialog
          open={inModifica !== null}
          onOpenChange={(aperto) => !aperto && setInModifica(null)}
        >
          <DialogContent>
            {inModifica && (
              <FormCompetizione
                key={inModifica.id ?? 'nuova'}
                competizione={inModifica}
                onChiudi={() => setInModifica(null)}
                onSalvata={async () => {
                  setInModifica(null);
                  await ricarica();
                }}
              />
            )}
          </DialogContent>
        </Dialog>
        {conferma}
      </Corpo>
    </>
  );
}

function FormCompetizione({
  competizione,
  onChiudi,
  onSalvata,
}: {
  competizione: Partial<Competizione>;
  onChiudi: () => void;
  onSalvata: () => Promise<void>;
}) {
  const [nome, setNome] = useState(competizione.nome ?? '');
  const [attiva, setAttiva] = useState(competizione.attiva ?? true);
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const partite = competizione.partite ?? 0;
  const rinominata =
    competizione.id !== undefined &&
    normalizzaNome(nome) !== '' &&
    normalizzaNome(nome) !== competizione.nome;

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = competizione.id
      ? modificaCompetizioneSchema.safeParse({ nome, attiva })
      : creaCompetizioneSchema.safeParse({ nome });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(competizione.id ? `/competizioni/${competizione.id}` : '/competizioni', {
        metodo: competizione.id ? 'PUT' : 'POST',
        corpo: esito.data,
      });
      await onSalvata();
      toast.success(`${esito.data.nome ?? nome} salvata.`);
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>
          {competizione.id ? `Modifica ${competizione.nome}` : 'Nuova competizione'}
        </DialogTitle>
        <DialogDescription>
          Il nome non contiene l'anno, che è della stagione della partita.
        </DialogDescription>
      </DialogHeader>

      <FieldGroup>
        <Field data-invalid={Boolean(errori.nome)}>
          <FieldLabel htmlFor="competizione-nome">Nome</FieldLabel>
          <Input
            id="competizione-nome"
            value={nome}
            aria-invalid={Boolean(errori.nome)}
            onChange={(e) => setNome(e.target.value)}
          />
          {errori.nome ? (
            <FieldError>{errori.nome}</FieldError>
          ) : nomeConAnno(nome) ? (
            <FieldDescription>{AVVISO_ANNO}</FieldDescription>
          ) : null}
        </Field>

        {competizione.id && (
          <Field orientation="horizontal">
            <FieldContent>
              <FieldLabel htmlFor="competizione-attiva">Attiva</FieldLabel>
              <FieldDescription>
                Una competizione disattivata resta sulle sue partite e nei filtri, ma non si propone
                per le partite nuove.
              </FieldDescription>
            </FieldContent>
            <Switch id="competizione-attiva" checked={attiva} onCheckedChange={setAttiva} />
          </Field>
        )}
      </FieldGroup>

      {rinominata && partite > 0 && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Nome aggiornato ovunque</AlertTitle>
          <AlertDescription>
            {partite === 1
              ? 'Il nuovo nome compare anche sulla partita già registrata.'
              : `Il nuovo nome compare anche sulle ${partite} partite già registrate.`}
          </AlertDescription>
        </Alert>
      )}

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          {competizione.id ? 'Salva' : 'Crea competizione'}
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
